/**
 * rosterOS Design System — Phase 1: Design System Foundation
 *
 * Tokens for colors, typography, spacing, radius, and shadows.
 * Import this file anywhere you need design system values.
 * The `buildTheme` function in App.tsx consumes these tokens.
 *
 * This file is the single source of truth for color, type, spacing, radius,
 * and shadow tokens. `constants.ts` re-exports the color maps for backward
 * compatibility — prefer importing from here.
 */

import { teamColorFg, teamColorBg } from "../helpers/colorHelpers";

// ── Brand Palette ─────────────────────────────────────────────────

export const brand = {
  primary: {
    light: "#1f3a1a",   // Signal brand green (anchor)
    dark: "#3a6b30",
  },
  secondary: {
    light: "#3D425E",
    dark: "#6B7194",
  },
  surface: {
    light: "#f3f5f8",   // cool page background (was warm cream)
    dark: "#111113",
  },
  paper: {
    light: "#FFFFFF",
    dark: "#1b1c1e",
  },
  paperElevated: {
    light: "#FFFFFF",
    dark: "#232427",
  },
  // Sidebar/drawer surface. Signal reads the nav as a clean surface with a
  // right hairline rather than a warm tint.
  sidebar: {
    light: "#FFFFFF",
    dark: "#161719",
  },
} as const;

// ── Semantic Palette ──────────────────────────────────────────────

export const semantic = {
  positive: "#0b7a34",   // Signal good (distinct from brand green)
  negative: "#dc2626",   // reserved for NEGATIVE numbers only
  warning: "#b45309",
  info: "#3B82F6",
  neutral: "#6B7280",
} as const;

// ── Position Colors ───────────────────────────────────────────────
// Canonical position palette. `constants.ts` re-exports this.

export const POSITION_COLORS: Record<string, string> = {
  // One position palette shared with the extension (Signal).
  QB: "#7b1fa2",
  RB: "#2e7d32",
  WR: "#e65100",
  TE: "#1565c0",
  K: "#9CA3AF",
  DST: "#6B7280",
};

// ── Team Colors ───────────────────────────────────────────────────
// Canonical NFL team palette. `constants.ts` re-exports this.

export const TEAM_COLORS: Record<string, string> = {
  ARI: "#97233F", ATL: "#A71930", BAL: "#241773", BUF: "#00338D", CAR: "#0085CA",
  CHI: "#0B162A", CIN: "#FB4F14", CLE: "#311D00", DAL: "#003594", DEN: "#002244",
  DET: "#0076B6", GB: "#203731", HOU: "#03202F", IND: "#002C5F", JAX: "#006778",
  KC: "#E31837", LV: "#000000", LAC: "#0080C6", LAR: "#003594", MIA: "#008E97",
  MIN: "#4F2683", NE: "#002244", NO: "#D3BC8D", NYG: "#0B2265", NYJ: "#125740",
  PHI: "#004C54", PIT: "#FFB612", SF: "#AA0000", SEA: "#002244", TB: "#D50A0A",
  TEN: "#4B92DB", WAS: "#5A1414",
};

// ── Color Accessors ───────────────────────────────────────────────
// Single entry points for position/team color, with dark-mode legibility
// handled via colorHelpers. `variant` selects text/fill vs background usage.

const NEUTRAL_FALLBACK = "#6B7280";

export function getPositionColor(position?: string): string {
  if (!position) return NEUTRAL_FALLBACK;
  return POSITION_COLORS[position.toUpperCase()] ?? NEUTRAL_FALLBACK;
}

export function getTeamColor(
  team: string | undefined,
  isDark: boolean,
  variant: "fg" | "bg" = "fg",
): string {
  const raw = team ? TEAM_COLORS[team.toUpperCase()] ?? NEUTRAL_FALLBACK : NEUTRAL_FALLBACK;
  return variant === "bg" ? teamColorBg(raw, isDark) : teamColorFg(raw, isDark);
}

// ── Surface Elevation (Dark Mode) ─────────────────────────────────

export const darkSurface = {
  level0: "#111113", // background
  level1: "#1b1c1e", // cards, sidebar
  level2: "#232427", // elevated cards, hover states
  level3: "#2b2d31", // popovers, dialogs
  level4: "#353535", // tooltips
} as const;

// ── Typography Scale ──────────────────────────────────────────────

export const typeScale = {
  display:     { fontSize: "28px", fontWeight: 700, lineHeight: 1.2 },
  heading:     { fontSize: "20px", fontWeight: 600, lineHeight: 1.3 },
  subheading:  { fontSize: "16px", fontWeight: 600, lineHeight: 1.4 },
  body:        { fontSize: "14px", fontWeight: 400, lineHeight: 1.5 },
  bodyStrong:  { fontSize: "14px", fontWeight: 600, lineHeight: 1.5 },
  caption:     { fontSize: "12px", fontWeight: 400, lineHeight: 1.4 },
  metric:      { fontSize: "24px", fontWeight: 700, lineHeight: 1.1 },
  metricSmall: { fontSize: "18px", fontWeight: 600, lineHeight: 1.2 },
  mono:        { fontSize: "13px", fontWeight: 500, lineHeight: 1.4, fontVariantNumeric: "tabular-nums" as const },
} as const;

export type TypeToken = keyof typeof typeScale;

/**
 * Returns a spreadable `sx` object for a typography token.
 * Use instead of hardcoding fontSize/fontWeight/lineHeight in components.
 * e.g. `<Typography sx={{ ...typeVariant("heading") }}>`
 */
export function typeVariant(token: TypeToken) {
  return { ...typeScale[token] };
}

// ── Spacing Tokens ────────────────────────────────────────────────

export const space = {
  xs:  4,
  sm:  8,
  md:  16,
  lg:  24,
  xl:  32,
  "2xl": 48,
} as const;

// ── Radius Tokens ─────────────────────────────────────────────────

export const radius = {
  sm: 6,
  md: 10,
  lg: 16,
} as const;

// ── Motion ────────────────────────────────────────────────────────
// One easing + a small duration scale so transitions feel uniform.

export const motion = {
  easing: "cubic-bezier(0.4, 0, 0.2, 1)", // ease-out
  fast: "120ms",
  base: "200ms",
  slow: "300ms",
} as const;

/** Convenience builder: `transition(["box-shadow", "transform"])`. */
export function transition(props: string[], duration: string = motion.base): string {
  return props.map(p => `${p} ${duration} ${motion.easing}`).join(", ");
}

// ── Shadow System ─────────────────────────────────────────────────

export const shadows = {
  card:         "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
  cardHover:    "0 4px 12px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04)",
  dropdown:     "0 8px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
  stickyHeader: "0 2px 8px rgba(0,0,0,0.06)",
} as const;

/* ────────────────────────────────────────────────────────────────────────
 * Signal reconciled token set (design handoff 2026-07-12).
 *
 * The objects above keep their original shapes so existing consumers don't
 * break; these are the fuller, per-theme tokens the overhauled primitives and
 * pages consume directly. Semantic laws:
 *  - STATUS.bad (#dc2626) is for NEGATIVE numbers/deltas ONLY (never a
 *    position, filter, or chart series).
 *  - One POSITION palette across the overlay + app.
 *  - No grade tokens in the visualizer — a grade is roster-conditional and
 *    meaningless portfolio-wide (owner decision 2026-07-12).
 * ──────────────────────────────────────────────────────────────────────── */

export const BRAND = {
  green: "#1f3a1a",        // anchor (buttons, logo, primary)
  greenHover: "#142611",
  greenLight: "#356b2b",   // on-light accents, links, active nav, chart line
  greenOnDark: "#6db85b",  // lighter variant for legibility on dark surfaces
} as const;

// Alias of POSITION_COLORS in the design's object shape (QB/RB/WR/TE only).
export const POSITION = {
  QB: "#7b1fa2",
  RB: "#2e7d32",
  WR: "#e65100",
  TE: "#1565c0",
} as const;

// Signal badges — solid fills, white text. LEV is magenta so it can never read
// as the QB position violet.
export const SIGNAL = {
  STK: "#0d9488",
  LEV: "#a3238e",
  QB: "#7b1fa2",
  BYE: "#c2410c",
  BYE_SEVERE: "#8a2708",
} as const;

// Draft platforms — yellow for Underdog, green for DraftKings, echoing how each
// site brands itself so the mapping needs no learning.
//
// Each platform carries a per-theme accent AND the ink to print ON it. That
// second value is not optional: a chip is a solid fill with a label, and while
// white reads fine on the light-mode golds and greens, it is unreadable on the
// bright dark-mode yellow (#facc15 + white is 1.5:1). Every pair below clears
// WCAG AA (4.5:1) both against the page surface and for its own label.
//
// The green is also pitched DARKER than the gold on light, and LIGHTER than the
// yellow on dark, on purpose. Gold-vs-green is the red-green confusion axis, so
// hue alone would not separate them for a colour-blind viewer or in greyscale;
// the deliberate luminance gap does. platformTheme.test.ts asserts it.
//
// Trade-off, accepted deliberately: the DraftKings green is in the same family
// as the `good` CLV green, so platform chips that sit beside a colored CLV
// number are rendered `outlined` rather than solid to keep the two roles
// visually separate.
export const PLATFORM = {
  Underdog:   { light: "#a16207", dark: "#facc15", onLight: "#ffffff", onDark: "#1c1400", short: "UD" },
  DraftKings: { light: "#166534", dark: "#22c55e", onLight: "#ffffff", onDark: "#04220f", short: "DK" },
  Unknown:    { light: "#6B7280", dark: "#9CA3AF", onLight: "#ffffff", onDark: "#16181c", short: "CSV" },
} as const;

function platformEntry(provider: string | undefined) {
  return PLATFORM[(provider ?? "") as keyof typeof PLATFORM] ?? PLATFORM.Unknown;
}

/** Platform accent for the active theme — legible as text, stroke or fill on
 *  that theme's surfaces. Unknown labels fall back to neutral. */
export function getPlatformColor(provider: string | undefined, isDark: boolean): string {
  const entry = platformEntry(provider);
  return isDark ? entry.dark : entry.light;
}

/** Ink to print ON `getPlatformColor` — required wherever the platform color is
 *  a background, since white fails on the dark-theme yellow. */
export function getPlatformOnColor(provider: string | undefined, isDark: boolean): string {
  const entry = platformEntry(provider);
  return isDark ? entry.onDark : entry.onLight;
}

/** Compact platform label for chips and split column headers ("UD" / "DK"). */
export function getPlatformShortLabel(provider: string | undefined): string {
  return (PLATFORM[(provider ?? "") as keyof typeof PLATFORM] ?? PLATFORM.Unknown).short;
}

// Deltas / status. `bad` is negatives-only. On dark, brighten (see DARK below).
export const STATUS = {
  good: "#0b7a34",
  bad: "#dc2626",
  warn: "#b45309",
} as const;

// Per-theme neutrals + surfaces (Signal cool neutrals, not the old warm cream).
export const LIGHT = {
  bg: "#f3f5f8",
  surface: "#ffffff",
  surface2: "#f6f8fa",
  sidebar: "#ffffff",
  hover: "#f0f3f6",
  expanded: "#eef4ec",     // expanded PlayerRow (brand-tinted)
  border: "#e3e7ec",
  borderLight: "#eef1f4",
  rail: "#e0e4e9",
  text: "#0f1420",
  textLight: "#535b69",
  caption: "#79818d",
  dim: "#a7aeb9",
  brand: BRAND.green,
  brandLight: BRAND.greenLight,
  accentSoft: "#e9f1e6",   // active-nav / pill tint
  accentLine: "#356b2b",   // 3px active-nav rail
  good: "#0b7a34",
  bad: "#dc2626",
  warn: "#b45309",
  shadow: "0 1px 2px rgba(16,20,28,.05)",
  shadowLg: "0 10px 34px -8px rgba(16,20,28,.14),0 2px 6px rgba(16,20,28,.05)",
} as const;

export const DARK = {
  bg: "#111113",
  surface: "#1b1c1e",
  surface2: "#232427",
  sidebar: "#161719",
  hover: "#26282b",
  expanded: "#1b271a",
  border: "#2b2d31",
  borderLight: "#232427",
  rail: "#2f3135",
  text: "#f2f3f5",
  textLight: "#a8afba",
  caption: "#818893",
  dim: "#5a616c",
  brand: "#3a6b30",
  brandLight: BRAND.greenOnDark,
  accentSoft: "#1c2919",
  accentLine: "#4f9142",
  good: "#3fce85",         // brightened for dark
  bad: "#f26b6b",
  warn: "#e0983a",
  shadow: "0 1px 2px rgba(0,0,0,.45)",
  shadowLg: "0 12px 36px -6px rgba(0,0,0,.6)",
} as const;

/** Pick the theme neutral set for the active mode. */
export const surfaceTokens = (darkMode: boolean) => (darkMode ? DARK : LIGHT);

// Bye/playoff schedule cells (Schedule page frozen grid).
export const SCHEDULE_CELLS = {
  light: {
    bye: { bg: "#fbe4cf", fg: "#b45309" },
    byePlayoff: { bg: "#f5c98f", fg: "#8a4708" },
    playoffBand: "#e9f1e6",
  },
  dark: {
    bye: { bg: "#3a2a12", fg: "#e0983a" },
    byePlayoff: { bg: "#4a3316", fg: "#e8a94a" },
    playoffBand: "#1c2919",
  },
} as const;
